import { useRef, useState, useEffect } from 'react';
import type { RefObject } from 'react';
import { ArrowUp } from 'lucide-react';
import { MainBodyBackdrop } from './layout/HoldingBackdrop';
import Header from './components/Header';
import About from './components/About';
import Services from './components/Services';
import Stats from './components/Stats';
import Partners from './components/Partners';
import Locations from './components/Locations';
import Security from './components/Security';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

export default function App() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  const aboutRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const locationsRef = useRef<HTMLElement>(null);
  const securityRef = useRef<HTMLElement>(null);
  const contactRef = useRef<HTMLElement>(null);

  const refs = {
    about: aboutRef,
    services: servicesRef,
    locations: locationsRef,
    security: securityRef,
    contact: contactRef,
  };

  const scrollToSection = (target: RefObject<HTMLElement>) => {
    target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px',
      }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#070b12] text-[#e8ede4]">
      <Header onNavigate={scrollToSection} refs={refs} />

      <div className="relative isolate">
        <MainBodyBackdrop />

        <Stats />
        <Partners />
        <About ref={aboutRef} />
        <Services ref={servicesRef} />
        <Security ref={securityRef} />
        <Locations ref={locationsRef} />
        <Footer ref={contactRef} />
      </div>

      <CookieConsent />

      <button
        type="button"
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-40 rounded-full border border-teal-200/35 bg-teal-100 px-4 py-4 text-[#07101d] shadow-lg shadow-black/30 transition-all duration-300 hover:bg-teal-50 ${
          showScrollTop ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-16 opacity-0'
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-6 w-6" aria-hidden />
      </button>
    </div>
  );
}
